/**
 * Deriving readable colours from a category's colour.
 *
 * The category header is painted in its own colour, and the subcategory
 * headings beneath it in a related shade, so scrolling the dashboard shows
 * where one category ends and the next begins without reading a word.
 *
 * Everything here is computed rather than picked, because the colours come
 * from a colour picker the user controls. Any pair of choices has to stay
 * legible, including the ones nobody would choose deliberately.
 */

interface Rgb {
  r: number
  g: number
  b: number
}

function parseHex(hex: string): Rgb | null {
  const cleaned = hex.trim().replace('#', '')
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(expanded)) return null
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  }
}

/**
 * Defaults for categories with no colour chosen.
 *
 * A single fallback would paint every header the same, which is exactly the
 * distinction the coloured headers exist to provide -- so an uncoloured
 * Docket would look no different from one without the feature. These are
 * assigned by category id, so a category keeps its colour for life rather than
 * shuffling when another is added or reordered.
 *
 * All are dark enough to take white text, so the headers read as one system
 * whether or not anything has been picked. The first two are the brand's own.
 */
export const CATEGORY_PALETTE = [
  '#004077', // navy, brand
  '#a90533', // crimson, brand
  '#0f766e', // teal
  '#b45309', // amber
  '#5b21b6', // violet
  '#15803d', // green
  '#334155', // slate
  '#9f1239', // rose
] as const

export const DEFAULT_CATEGORY_COLOR = CATEGORY_PALETTE[0]

/** The colour a category falls back to, stable for a given id. */
export function fallbackCategoryColor(categoryId: number): string {
  const index = Math.abs(Math.trunc(categoryId)) % CATEGORY_PALETTE.length
  return CATEGORY_PALETTE[index] ?? DEFAULT_CATEGORY_COLOR
}

/** The colour actually used for a category: its own, or its stable default. */
export function effectiveCategoryColor(
  color: string | null | undefined,
  categoryId: number,
): string {
  return color && parseHex(color) ? color : fallbackCategoryColor(categoryId)
}

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

/**
 * WCAG relative luminance.
 *
 * Not the naive average of the channels: the eye is far more sensitive to
 * green than to blue, so a mid grey and a saturated blue of the "same"
 * brightness need different text colours. Using the proper coefficients is the
 * difference between white-on-amber being readable and being a smear.
 */
export function luminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0
  const channel = (value: number) => {
    const v = value / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return (
    0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
  )
}

/** Contrast ratio between two colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la]
  return (lighter + 0.05) / (darker + 0.05)
}

export const INK = '#1a1a1a'
export const PAPER = '#ffffff'

/**
 * Black or white text, whichever is actually more readable on `background`.
 *
 * Chosen by comparing both contrast ratios rather than by a luminance
 * threshold. A fixed threshold gets the mid-tones wrong in exactly the range
 * -- mid blues, ambers -- where a colour picker invites people to go.
 */
export function readableTextOn(background: string): string {
  return contrastRatio(background, INK) >= contrastRatio(background, PAPER)
    ? INK
    : PAPER
}

export function isLight(hex: string): boolean {
  return luminance(hex) > 0.5
}

function mix(hex: string, towards: string, amount: number): string {
  const from = parseHex(hex)
  const to = parseHex(towards)
  if (!from || !to) return hex
  return toHex({
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  })
}

/** The subcategory band must be legible in its own right. */
const MIN_TEXT_CONTRAST = 4.5

/**
 * How far apart the two bands must read.
 *
 * Deliberately large. A minimal separation is technically distinguishable and
 * looks like a mistake -- two nearly identical blues stacked on each other. The
 * subcategory band is meant to be an obvious pale tint of the category, not a
 * slightly different shade of it.
 */
const MIN_BAND_SEPARATION = 3.5

/** Where the search starts, i.e. how far the tint goes before it is checked. */
const START_MIX = { light: 0.34, dark: 0.84 }

/**
 * The subcategory band: the same hue, well clear of the category's.
 *
 * A dark category gets a *pale tint* of itself with dark text, not a slightly
 * lighter dark. A light category gets a clearly darker shade. Either way the
 * two bands share a hue and could not be mistaken for each other.
 *
 * The amount is searched rather than fixed, because a single step that looks
 * right for a mid blue fails at the ends: near the extremes there is little
 * room to move, and near the mid-tones the result can land where neither black
 * nor white text is comfortable. The sweep is a few arithmetic operations and
 * turns the guarantee from approximate into actual.
 */
export function subcategoryShade(categoryColor: string): string {
  const light = isLight(categoryColor)
  const towards = light ? INK : PAPER
  const start = light ? START_MIX.light : START_MIX.dark

  let fallback = mix(categoryColor, towards, start)
  for (let amount = start; amount <= 0.94; amount += 0.02) {
    const candidate = mix(categoryColor, towards, amount)
    fallback = candidate
    const legible =
      contrastRatio(candidate, readableTextOn(candidate)) >= MIN_TEXT_CONTRAST
    const distinct = contrastRatio(candidate, categoryColor) >= MIN_BAND_SEPARATION
    if (legible && distinct) return candidate
  }
  // Only reachable for colours already at an extreme, where the largest step
  // is the best answer available.
  return fallback
}

/** Background and text for a category header, from its (possibly unset) colour. */
export function categoryHeaderStyle(
  color: string | null | undefined,
  categoryId: number,
) {
  const background = effectiveCategoryColor(color, categoryId)
  return { background, text: readableTextOn(background) }
}

/** Background and text for a subcategory heading under that category. */
export function subcategoryHeaderStyle(
  color: string | null | undefined,
  categoryId: number,
) {
  const background = subcategoryShade(effectiveCategoryColor(color, categoryId))
  return { background, text: readableTextOn(background) }
}
